// İSG uzmanı portalı — Kiosk Cihaz Yönetimi (api-contract §5.9). Yönetim paneli altında
// "Kiosk Cihazları" bölümü. Cihaz listesi (UTable sözleşmesi: page/pageSize/search +
// total/filteredTotal), kayıt (KioskRegisterModal → tek-kullanımlık activationCode) ve
// iptal/devre-dışı (`POST /auth/kiosk/{uuid}/revoke` — ConfirmDialog reuse).
//
// GİZLİ VERİ: Liste (§5.9.5) activationCode/secret/token **taşımaz** — bu ekran yalnız
// yönetim meta'sını (ad/saha/durum/rol/son görülme) gösterir. activationCode YALNIZ kayıt
// akışında (KioskRegisterModal) tek-sefer gösterilir.

import {
  Alert,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  EmptyState,
  type KioskDeviceDto,
  Pagination,
  RowActionsMenu,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  Tag,
  adminApi,
  currentCulture,
  kioskApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MonitorSmartphone, Plus, SearchX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KioskRegisterModal } from '../../components/admin/KioskRegisterModal';
import { ShellHeaderAction } from '../../components/layout/ShellHeaderAction';
import { kioskStatusTagColor } from '../../components/status/statusTagColors';
import { formatDate, formatDateTime } from '../../lib/format';
import { kioskKeys } from '../../lib/kioskKeys';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

const PAGE_SIZE = 20;
const ROLE_LOOKUP_PAGE_SIZE = 200;

/** İptal hedefi — `disable=false` yalnız oturumları iptal; `true` cihazı devre dışı bırakır. */
interface RevokeTarget {
  device: KioskDeviceDto;
  disable: boolean;
}

export function KioskDevicesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const culture = currentCulture();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<string | null>(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null);

  function onSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  const params = { page, pageSize: PAGE_SIZE, search: debouncedSearch };

  const query = useQuery({
    queryKey: kioskKeys.devices(params),
    queryFn: () => kioskApi.listDevices(params),
    placeholderData: (prev) => prev,
  });

  // Rol adları — liste yalnız `roleUuids` taşır (§5.9.5). uuid→ad eşlemesi için aktif
  // rol kataloğu bir kez çekilir (yönetim paneli zaten `admin/roles` yetkili).
  const rolesQuery = useQuery({
    queryKey: ['kiosk', 'role-names'] as const,
    queryFn: () =>
      adminApi.listRoles({
        page: 1,
        pageSize: ROLE_LOOKUP_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      }),
    staleTime: 5 * 60_000,
  });

  const roleNameByUuid = useMemo(() => {
    const map = new Map<string, string>();
    for (const role of rolesQuery.data?.items ?? []) {
      map.set(role.uuid, role.name);
    }
    return map;
  }, [rolesQuery.data]);

  function roleLabels(device: KioskDeviceDto): string {
    if (device.roleUuids.length === 0) {
      return '—';
    }
    return device.roleUuids.map((uuid) => roleNameByUuid.get(uuid) ?? uuid.slice(0, 8)).join(', ');
  }

  const revokeMutation = useMutation({
    mutationFn: (target: RevokeTarget) =>
      kioskApi.revoke(target.device.uuid, { disable: target.disable }),
    onSuccess: (_result, target) => {
      queryClient.invalidateQueries({ queryKey: kioskKeys.devices() });
      setMessage(
        target.disable
          ? t('kiosk.revoke.disabledMessage', { name: target.device.name })
          : t('kiosk.revoke.revokedMessage', { name: target.device.name }),
      );
      setRevokeTarget(null);
    },
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(debouncedSearch);
  function clearFilters() {
    setSearch('');
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-5">
      <ShellHeaderAction>
        <Button
          onClick={() => {
            setMessage(null);
            setRegisterOpen(true);
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('kiosk.register.new')}
        </Button>
      </ShellHeaderAction>

      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={query.data ? t('kiosk.count', { count: query.data.filteredTotal }) : undefined}
            search={search}
            onSearchChange={onSearchChange}
            searchLabel={t('admin.common.search')}
            searchPlaceholder={t('kiosk.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
          />

          <Table caption={t('kiosk.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('kiosk.col.name')}</TableHead>
                <TableHead>{t('kiosk.col.site')}</TableHead>
                <TableHead>{t('kiosk.col.status')}</TableHead>
                <TableHead>{t('kiosk.col.roles')}</TableHead>
                <TableHead>{t('kiosk.col.lastSeen')}</TableHead>
                <TableHead>{t('kiosk.col.createdAt')}</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">{t('admin.common.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={7} />
              ) : query.isError ? (
                <TableMessageRow colSpan={7}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={7}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('kiosk.list.noResults')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={MonitorSmartphone}
                      title={t('kiosk.empty')}
                      description={t('kiosk.empty.body')}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMessage(null);
                            setRegisterOpen(true);
                          }}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('kiosk.register.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((device) => (
                  <TableRow key={device.uuid}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MonitorSmartphone className="h-4 w-4 text-muted" aria-hidden="true" />
                        <span className="font-medium">{device.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted">{device.siteName ?? '—'}</TableCell>
                    <TableCell>
                      <Tag color={kioskStatusTagColor(device.status)}>
                        {t(`kiosk.status.${device.status}`)}
                      </Tag>
                    </TableCell>
                    <TableCell className="text-muted">{roleLabels(device)}</TableCell>
                    <TableCell className="text-muted">
                      {device.lastSeenAt ? formatDateTime(device.lastSeenAt, culture) : '—'}
                    </TableCell>
                    <TableCell className="text-muted">
                      {formatDate(device.createdAt, culture)}
                    </TableCell>
                    <TableCell className="w-12 px-2">
                      {/* S33 Slice 4 QA fix, defekt #4 — bu satırın ayrı bir "aç" hedefi yok
                          (kiosk cihazı düzenlenmez, yalnız kaydedilir/iptal edilir); iki eylem de
                          ⋮ menüsüne taşındı. */}
                      <RowActionsMenu
                        ariaLabel={t('kiosk.action.openMenu', { name: device.name })}
                        items={[
                          {
                            key: 'revoke',
                            label: t('kiosk.action.revokeSessions'),
                            disabled: device.status === 'Disabled',
                            onSelect: () => {
                              setMessage(null);
                              setRevokeTarget({ device, disable: false });
                            },
                          },
                          {
                            key: 'disable',
                            label: t('kiosk.action.disable'),
                            destructive: true,
                            disabled: device.status === 'Disabled',
                            onSelect: () => {
                              setMessage(null);
                              setRevokeTarget({ device, disable: true });
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('admin.pagination.nav'),
                previous: t('admin.pagination.previous'),
                next: t('admin.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <KioskRegisterModal
        open={registerOpen}
        onOpenChange={setRegisterOpen}
        onSuccess={() => setMessage(t('kiosk.register.createdMessage'))}
      />

      <ConfirmDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        title={
          revokeTarget?.disable ? t('kiosk.revoke.disableTitle') : t('kiosk.revoke.revokeTitle')
        }
        body={
          revokeTarget?.disable
            ? t('kiosk.revoke.disableBody', { name: revokeTarget?.device.name ?? '' })
            : t('kiosk.revoke.revokeBody', { name: revokeTarget?.device.name ?? '' })
        }
        confirmLabel={
          revokeTarget?.disable ? t('kiosk.revoke.disableConfirm') : t('kiosk.revoke.revokeConfirm')
        }
        confirmVariant={revokeTarget?.disable ? 'danger' : 'primary'}
        loading={revokeMutation.isPending}
        error={revokeMutation.isError ? revokeMutation.error : undefined}
        onConfirm={() => revokeTarget && revokeMutation.mutate(revokeTarget)}
      />
    </div>
  );
}
